import axios from 'axios';
import toast from 'react-hot-toast';

// Configuration de base d'Axios
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Intercepteur pour ajouter le token d'authentification
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Intercepteur pour gérer les erreurs et le refresh token
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refreshToken
          });
          
          const { tokens } = response.data;
          localStorage.setItem('accessToken', tokens.accessToken);
          localStorage.setItem('refreshToken', tokens.refreshToken);
          
          // Réessayer la requête originale avec le nouveau token
          originalRequest.headers.Authorization = `Bearer ${tokens.accessToken}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        // Refresh token invalide, rediriger vers login
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    // Afficher les erreurs sous forme de toast (sauf pour les erreurs 401 gérées ci-dessus)
    if (error.response?.status !== 401) {
      const message = error.response?.data?.error || 'Une erreur est survenue';
      if (!originalRequest._skipErrorToast) {
        toast.error(message);
      }
    }

    return Promise.reject(error);
  }
);

// Services API
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  logout: () => api.post('/auth/logout'),
  refreshToken: (refreshToken) => api.post('/auth/refresh', { refreshToken }),
  getProfile: () => api.get('/auth/me'),
  register: (userData) => api.post('/auth/register', userData),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, newPassword) => api.post('/auth/reset-password', { token, newPassword }),
  verifyResetToken: (token) => api.get(`/auth/verify-reset-token/${token}`),
};

export const userAPI = {
  getUsers: (params) => api.get('/users', { params }),
  getUser: (userId) => api.get(`/users/${userId}`),
  createUser: (data) => api.post('/users', data),
  updateUser: (userId, data) => api.put(`/users/${userId}`, data),
  updateUserStatus: (userId, status) => api.put(`/users/${userId}/status`, { status }),
  changePassword: (userId, passwords) => api.put(`/users/${userId}/password`, passwords),
  getMutualistes: () => api.get('/mutualistes/available-for-users'),
};

export const offerAPI = {
  getOffers: (params) => api.get('/offers', { params }),
  getOffer: (offerId) => api.get(`/offers/${offerId}`),
  createOffer: (data) => api.post('/offers', data),
  updateOffer: (offerId, data) => api.put(`/offers/${offerId}`, data),
};

export const mutualisteAPI = {
  getProfile: () => api.get('/mutualistes/me'),
  getAll: (params) => api.get('/mutualistes', { params }),
  search: (params) => api.get('/mutualistes/search', { params }),
  getStatistics: (params) => api.get('/mutualistes/statistics', { params }),
  getSegmentStats: () => api.get('/mutualistes/stats/segments'),
  createMutualiste: (data) => api.post('/mutualistes', data),
  searchMutualistes: (params) => api.get('/mutualistes/search', { params }),
  getMutualiste: (id) => api.get(`/mutualistes/${id}`),
  updateMutualiste: (id, data) => api.put(`/mutualistes/${id}`, data),
  getAyantsDroit: (id) => api.get(`/mutualistes/${id}/ayants-droit`),
  addAyantDroit: (id, data) => api.post(`/mutualistes/${id}/ayants-droit`, data),
  getPolicies: (id) => api.get(`/mutualistes/${id}/policies`),
};

// Ancienne déclaration supprimée - voir la nouvelle plus complète en bas du fichier

export const subscriptionAPI = {
  getSubscriptions: (params) => api.get('/subscriptions', { params }),
  getAllSubscriptions: (params) => api.get('/subscriptions/all', { params }),
  getSubscription: (id) => {
    const timestamp = Date.now();
    return api.get(`/subscriptions/${id}?_t=${timestamp}`);
  },
  createSubscription: (data) => api.post('/subscriptions', data),
  updateSubscription: (id, data) => api.put(`/subscriptions/${id}`, data),
  cancelSubscription: (id, data) => api.put(`/subscriptions/${id}/cancel`, data),
  approveSubscription: (id, data) => api.put(`/subscriptions/${id}/approve`, data),
  rejectSubscription: (id, data) => api.put(`/subscriptions/${id}/reject`, data),
  createSubscriptionForUser: (data) => api.post('/subscriptions/admin/create', data),
  getStats: (params) => api.get('/subscriptions/stats/overview', { params }),
};

export const policyAPI = {
  getPolicies: (params) => api.get('/policies', { params }),
  getPolicy: (id) => api.get(`/policies/${id}`),
  getPolicyDetails: (id) => api.get(`/policies/${id}`),
  getPolicySavingsDetails: (id) => api.get(`/policies/${id}/savings-details`),
  updatePolicyStatus: (id, data) => api.put(`/policies/${id}/status`, data),
  getStats: (params) => api.get('/policies/stats/overview', { params }),
  getUserPolicies: (params) => api.get('/policies/user', { params }),
  getUserContributions: (params) => api.get('/contributions/user', { params }),
  payContribution: (id, data) => api.put(`/contributions/${id}/pay`, data),
};

export const historyAPI = {
  getHistory: (userId, params) => api.get(`/histories/${userId}`, { params }),
  getEntityHistory: (entityType, entityId) => api.get(`/histories/entity/${entityType}/${entityId}`),
  exportHistory: (userId, params) => api.get(`/histories/${userId}/export`, { params }),
  getHistoryStats: (userId, params) => api.get(`/histories/${userId}/stats`, { params }),
};

export const notificationAPI = {
  getNotifications: (params) => api.get('/notifications', { params }),
  getAdminNotifications: (params) => api.get('/notifications/admin', { params }),
  markAsRead: (notificationIds) => api.put('/notifications/mark-read', { notificationIds }),
  markAllAsRead: () => api.put('/notifications/mark-all-read'),
  createNotification: (data) => api.post('/notifications', data),
  deleteNotification: (notificationId) => api.delete(`/notifications/${notificationId}`),
};

// Types de demandes (requests)
export const requestTypeAPI = {
  getRequestTypes: (params) => api.get('/request-types', { params }),
  getRequestType: (id) => api.get(`/request-types/${id}`),
};

export const requestAPI = {
  createRequest: (data) => api.post('/requests', data),
  getRequests: (params) => api.get('/requests', { params }),
  getUserRequests: (params) => api.get('/requests/user', { params }),
  getRequest: (id) => api.get(`/requests/${id}`),
  approveRequest: (id, data) => api.put(`/requests/${id}/approve`, data),
  rejectRequest: (id, data) => api.put(`/requests/${id}/reject`, data),
  getStats: (params) => api.get('/requests/stats/overview', { params }),
};

// Motifs génériques (reasons)
export const reasonsAPI = {
  getReasons: (params) => api.get('/reasons', { params }),
  getReason: (id) => api.get(`/reasons/${id}`),
};

// Services et remboursements santé
export const healthServiceAPI = {
  getHealthService: (id) => api.get(`/health-services/${id}`),
  createHealthService: (data) => api.post('/health-services', data),
  getHealthServiceTypes: () => api.get('/health-services/types'),
};

export const healthReimbursementAPI = {
  createHealthReimbursement: (data) => api.post('/health-reimbursements', data),
  getHealthReimbursement: (id) => api.get(`/health-reimbursements/${id}`),
  getHealthReimbursements: (params) => api.get('/health-reimbursements', { params }),
};

// Uploads
export const uploadAPI = {
  uploadHealthReimbursementFiles: (formData) =>
    api.post('/uploads/health-reimbursements', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
};

// Utilitaires
export const getFileUrl = (relativePath) => {
  if (!relativePath) return '';
  // API_BASE_URL est de la forme http://host:port/api → on retire le suffixe /api
  const backendBase = API_BASE_URL.replace(/\/?api\/?$/, '');
  return `${backendBase}${relativePath}`;
};

export const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return 'XOF 0';
  const number = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (isNaN(number)) return 'XOF 0';
  return `XOF ${number.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
};

export const formatDate = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

export const formatDateTime = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
};

export const getStatusColor = (status) => {
  const colors = {
    // Anciens codes (compatibilité)
    ACTIVE: 'success',
    PENDING: 'warning',
    SUSPENDED: 'error',
    CANCELLED: 'error',
    EXPIRED: 'error',
    // Nouveaux libellés français
    'Validée': 'success',
    'En attente': 'warning',
    'En traitement': 'info',
    'Rejetée': 'error',
    'Annulée': 'error',
    'Suspendu(e)': 'error',
    'Radié(e)': 'error',
    'Actif(ve)': 'success',
  };
  return colors[status] || 'info';
};

export const getStatusLabel = (status) => {
  const labels = {
    // Anciens codes (compatibilité)
    ACTIVE: 'Active',
    PENDING: 'Soumis',
    SUSPENDED: 'Suspendu',
    CANCELLED: 'Annulé',
    EXPIRED: 'Expiré',
    // Nouveaux libellés français (retournent eux-mêmes)
    'Validée': 'Validée',
    'En attente': 'En attente',
    'En traitement': 'En traitement',
    'Rejetée': 'Rejetée',
    'Annulée': 'Annulée',
    'Suspendu(e)': 'Suspendu(e)',
    'Radié(e)': 'Radié(e)',
    'Actif(ve)': 'Actif(ve)',
  };
  return labels[status] || status;
};

export const getOfferTypeLabel = (type) => {
  const labels = {
    HEALTH_COVERAGE: 'Santé',
    SAVINGS: 'Épargne',
    LOAN: 'Prêt',
  };
  return labels[type] || type;
};

export const getOfferColor = (type) => {
  const colors = {
    HEALTH_COVERAGE: 'primary',
    SAVINGS: 'secondary', 
    LOAN: 'accent',
  };
  return colors[type] || 'primary';
};

export const getOfferIcon = (type) => {
  const icons = {
    HEALTH_COVERAGE: '🏥',
    SAVINGS: '💰',
    LOAN: '💳',
  };
  return icons[type] || '📄';
};

// ========== API PARAMÈTRES ==========

export const parameterAPI = {
  // Situations matrimoniales
  getSituationsMatrimoniales: (params = {}) => api.get('/parameters/situation-matrimoniale', { params }),
  getSituationMatrimonialeById: (id) => api.get(`/parameters/situation-matrimoniale/${id}`),
  createSituationMatrimoniale: (data) => api.post('/parameters/situation-matrimoniale', data),
  updateSituationMatrimoniale: (id, data) => api.put(`/parameters/situation-matrimoniale/${id}`, data),
  deleteSituationMatrimoniale: (id) => api.delete(`/parameters/situation-matrimoniale/${id}`),

  // Liens de parenté
  getLiensParente: (params = {}) => api.get('/parameters/lien-parente', { params }),
  getLienParenteById: (id) => api.get(`/parameters/lien-parente/${id}`),
  createLienParente: (data) => api.post('/parameters/lien-parente', data),
  updateLienParente: (id, data) => api.put(`/parameters/lien-parente/${id}`, data),
  deleteLienParente: (id) => api.delete(`/parameters/lien-parente/${id}`),

  // Fonctions
  getFonctions: (params = {}) => api.get('/parameters/fonctions', { params }),
  getFonctionById: (id) => api.get(`/parameters/fonctions/${id}`),
  createFonction: (data) => api.post('/parameters/fonctions', data),
  updateFonction: (id, data) => api.put(`/parameters/fonctions/${id}`, data),
  deleteFonction: (id) => api.delete(`/parameters/fonctions/${id}`),

  // Statuts
  getStatuts: (params = {}) => api.get('/parameters/status', { params }),
  getStatutById: (id) => api.get(`/parameters/statut-mutualiste/${id}`),
  createStatut: (data) => api.post('/parameters/statut-mutualiste', data),
  updateStatut: (id, data) => api.put(`/parameters/statut-mutualiste/${id}`, data),
  deleteStatut: (id) => api.delete(`/parameters/statut-mutualiste/${id}`),

  // Options pour formulaires
  getActiveOptions: () => api.get('/parameters/options'),
  
  // Statistiques
  getParametersStatistics: () => api.get('/parameters/statistics')
};

// API Enterprises
export const enterpriseAPI = {
  // CRUD de base
  getAll: (params = {}) => api.get('/enterprises', { params }),
  getById: (id) => api.get(`/enterprises/${id}`),
  create: (data) => api.post('/enterprises', data),
  update: (id, data) => api.put(`/enterprises/${id}`, data),
  delete: (id) => api.delete(`/enterprises/${id}`),
  
  // Actions spécifiques
  toggleStatus: (id) => api.put(`/enterprises/${id}/toggle-status`),
  
  // Options pour formulaires
  getOptions: () => api.get('/enterprises/options'),
  search: (params) => api.get('/enterprises/search', { params }),
  
  // Statistiques
  getStatistics: () => api.get('/enterprises/statistics')
};

// API Paiements Unifiés
export const paymentAPI = {
  // Catégories et raisons
  getCategories: () => api.get('/payments/categories'),
  getReasons: () => api.get('/payments/reasons'),
  getReasonsByCategory: (categoryCode) => api.get(`/payments/reasons/category/${categoryCode}`),
  
  // Création paiements
  createUnifiedPayment: (data) => api.post('/payments/unified', data),
  
  // Récupération
  getPaymentById: (id) => api.get(`/payments/${id}`),
  getPaymentHistory: (params) => api.get('/payments', { params }),
  getStats: (params) => api.get('/payments/stats/overview', { params }),
  getMonthlyEvolution: (params) => api.get('/payments/stats/monthly-evolution', { params }),
  
  // Documents
  generateVoucher: (id) => api.get(`/payments/${id}/voucher`),
  getReceiptData: (id) => api.get(`/payments/${id}/receipt-data`),
};

export { api };
export default api;
